import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Clock,
  RefreshCw,
  FileText,
  CheckCircle,
  AlertTriangle,
  CreditCard,
  Activity,
  Building
} from "lucide-react";
import { getFinancialApplications } from "../../services/financialservice";

const PendingDataTab = () => {
  const navigate = useNavigate();
  
  // State management
  const [pendingDataApps, setPendingDataApps] = useState([]);
  const [pendingDataLoading, setPendingDataLoading] = useState(false);
  const [pendingDataSearch, setPendingDataSearch] = useState("");
  const [pendingDataFilter, setPendingDataFilter] = useState('all');
  const [pendingDataCounts, setPendingDataCounts] = useState({});


  // Load finance domain applications for "All" tab
  const loadFinanceApplications = useCallback(async () => {
    setPendingDataLoading(true);
    try {
      const filters = { 
        searchTerm: pendingDataSearch,
        _fetchAll: true, // Fetch all applications and filter client-side
        // No status filter to get all finance-relevant applications
      };
      const result = await getFinancialApplications(
        filters,
        1,
        100 // Load more for counting
      );
      if (result.success) {
        // The financial service already filters for finance-relevant applications
        const financeApps = result.applications || [];
        
        // Debug: Log the raw data to understand what we're getting
        console.log('🔍 Raw financial service result:', {
          success: result.success,
          total: result.total,
          applicationsCount: financeApps.length,
          sampleApp: financeApps[0] ? {
            id: financeApps[0].id,
            status: financeApps[0].status,
            workflow: financeApps[0].workflow
          } : null
        });
        
        // If no applications found, try to get some sample data for debugging
        if (financeApps.length === 0) {
          console.log('⚠️ No finance applications found. This might indicate:');
          console.log('1. No applications in the database');
          console.log('2. Filtering is too restrictive');
          console.log('3. Data structure mismatch');
          console.log('4. Database connection issues');
        }
        
        setPendingDataApps(financeApps);
        
        // Calculate counts for all tabs - each application can be in multiple buckets
        const counts = {
          all: financeApps.length,
          new: 0,
          diagnosis_approved: 0,
          query_raised: 0,
          query_reply: 0,
          finance_approved: 0,
          finance_approval_pending: 0, // New: Finance approval pending
          dispersal_pending: 0, // New: Dispersal pending
          fal_generated: 0,
          enach_pending: 0,
          enach_completed: 0,
          scrutiny_pending: 0,
          pod_pending: 0,
        };

        financeApps.forEach((app) => {
          const w = app?.workflow || {};
          const fin = w.financialVerification || {};
          const ds = w.dischargeSettlement || {};
          const enach = w.enach || w.eNach || {};
          const dispatch = w.dispatch || w.dispatches || {};
          const pod = w.proofOfDispatch || w.pod || {};
          const mv = w.medicalVerification || {};
          const queries = w.queries || [];
          const disputes = w.disputes || [];
          
          // Skip approved applications from pending data counts (be more strict)
          const isApproved = (
            fin.status === 'approved' ||
            app?.status === 'financial_approved' ||
            app?.status === 'approved'
          );
          
          // Check if this application should be in dispersal pending (ONLY truly pending dispersal)
          const isDispersalPendingCount = (
            // Applications with explicit dispersal pending status
            (w.disbursal?.status === 'pending' || w.disbursal?.status === 'initiated') ||
            // Applications with dispersal workflow but not completed
            (w.disbursal?.status && w.disbursal?.status !== 'completed' && w.disbursal?.status !== 'disbursed') ||
            // Applications with FAL generated AND dispersal pending
            ((ds?.finalApprovalLetterUrl || ds?.finalApprovalLetterId) && w.disbursal?.status === 'pending') ||
            // Applications with eNACH completed AND dispersal pending
            ((enach?.status === 'completed' || enach?.completedAt) && w.disbursal?.status === 'pending') ||
            // Applications with POD received AND dispersal pending
            ((pod?.status === 'received' || pod?.acknowledgedAt) && w.disbursal?.status === 'pending') ||
            // Applications with dispatch completed AND dispersal pending
            ((dispatch?.status === 'completed' || dispatch?.status === 'dispatched') && w.disbursal?.status === 'pending') ||
            // Applications with financial approval AND dispersal pending
            ((fin.status === 'approved' || app?.status === 'financial_approved') && w.disbursal?.status === 'pending') ||
            // Applications with settlement approved AND dispersal pending
            ((app?.status === 'settlement_approved' || ds?.status === 'approved') && w.disbursal?.status === 'pending')
          );
          
          if (isDispersalPendingCount) {
            counts.dispersal_pending++;
          }
          
          // If this is an approved application, skip counting for other categories
          if (isApproved) {
            return;
          }

          // CREATIVE FILTERING - Be more inclusive and creative

          // 1. NEW APPLICATIONS - Any application that's new to finance
          const isNewApplication = (
            // Medical approved and just entered finance
            (app?.status === 'medical_approved' && (fin.createdAt || fin.assignedTo)) ||
            // Financial assessment stages (new to finance)
            (app?.status === 'financial_assessment' || app?.status === 'document_verification' || 
             app?.status === 'credit_analysis' || app?.status === 'under_financial_assessment') ||
            // Applications that just entered finance pipeline
            (app?.status === 'under_financial_review' || app?.status === 'financial_review_pending' ||
             app?.status === 'financial_verification_pending' || app?.status === 'awaiting_financial_approval') ||
            // Has finance workflow but no clear status yet
            (fin.createdAt && (!fin.status || fin.status === 'pending' || fin.status === 'under_review')) ||
            // Applications assigned to finance but not processed
            (fin.assignedTo && (!fin.status || fin.status === 'pending')) ||
            // Any application with financial workflow but not yet processed
            ((fin.createdAt || fin.assignedTo) && fin.status !== 'approved' && fin.status !== 'rejected' && 
             app?.status !== 'financial_approved' && app?.status !== 'financial_rejected') ||
            // Applications that are in early finance stages
            (app?.status === 'under_review' && fin.underReview) ||
            // Applications with pending financial verification
            (fin.status === 'pending' || fin.status === 'under_review')
          );
          
          if (isNewApplication) {
            counts.new++;
          }

          // 2. DIAGNOSIS APPROVED - Medical approved applications (be VERY creative)
          const isDiagnosisApproved = (
            app?.status === 'diagnosis_approved' ||
            app?.status === 'medical_approved' ||
            app?.status === 'approved' ||
            // Medical verification status
            (mv.status === 'approved' || mv.status === 'completed' || mv.status === 'verified') ||
            (mv.completedAt || mv.approvedAt || mv.verifiedAt) ||
            // Medical workflow completed
            (w.medicalVerification?.status === 'approved' || w.medicalVerification?.completedAt) ||
            // Any application that has completed medical verification
            (mv.status === 'approved' || mv.completedAt || mv.verifiedAt) ||
            // Applications that are ready for finance after medical approval
            (app?.status === 'medical_approved' && (fin.createdAt || fin.assignedTo || fin.underReview)) ||
            // Applications with medical verification completed
            (w.medicalVerification?.status === 'approved' || w.medicalVerification?.completedAt) ||
            // Any application with medical approval indicators
            (app?.medicalApproved || app?.diagnosisApproved || app?.medicalCleared) ||
            // Applications with medical review completed
            (w.medicalReview?.status === 'approved' || w.medicalReview?.completedAt) ||
            // Applications with doctor approval
            (w.doctorReview?.status === 'approved' || w.doctorReview?.completedAt) ||
            // Any application that has passed medical stage
            (app?.status === 'under_financial_review' && (mv.status === 'approved' || mv.completedAt)) ||
            // Applications with medical clearance
            (app?.medicalCleared || app?.diagnosisCleared) ||
            // Any application that has medical verification
            (mv.verifiedBy || mv.approvedBy || mv.completedBy) ||
            // Applications with medical documents approved
            (w.medicalDocuments?.status === 'approved' || w.medicalDocuments?.verified) ||
            // Fallback: Any application that might be medically approved
            (app?.status === 'under_review' && mv.status) ||
            (app?.status === 'pending_financial_clarification' && mv.status === 'approved') ||
            (fin.status && (mv.status === 'approved' || mv.completedAt))
          );
          
          if (isDiagnosisApproved) {
            counts.diagnosis_approved++;
          }

          // 3. QUERY RAISED - Finance has raised queries (FINANCE ONLY - be VERY creative)
          const isQueryRaised = (
            // STRICT FINANCE FILTER: Only finance-relevant applications
            (fin.status || fin.createdAt || fin.assignedTo || fin.underReview || fin.assessmentStarted || fin.documentReview || 
             w.financialVerification || w.dischargeSettlement || w.disbursal || w.enach || w.eNach || w.proofOfDispatch || w.pod || w.recovery ||
             app?.status === 'under_financial_review' || app?.status === 'financial_assessment' || app?.status === 'document_verification' ||
             app?.status === 'credit_analysis' || app?.status === 'under_financial_assessment' || app?.status === 'financial_review_pending' ||
             app?.status === 'financial_verification_pending' || app?.status === 'awaiting_financial_approval' || app?.status === 'financial_document_review') &&
            
            // EXCLUDE: Medical, Hospital, Policy, or other department queries
            !(w.medicalVerification || w.hospitalVerification || w.policyVerification || w.medicalWorkflow || w.hospitalWorkflow || w.policyWorkflow ||
              app?.status?.includes('medical') || app?.status?.includes('hospital') || app?.status?.includes('policy') ||
              queries.some(q => q.raisedBy === 'medical' || q.raisedBy === 'hospital' || q.raisedBy === 'policy')) &&
            
            // INCLUDE: Finance queries only
            (
            app?.status === 'pending_financial_clarification' ||
            app?.status === 'pending_clarification' ||
            app?.status === 'query_pending' ||
            app?.status === 'awaiting_clarification' ||
            // Any application with pending queries from finance
            (queries.length > 0 && queries.some(q => q.raisedBy === 'finance' && q.status === 'pending')) ||
            // Applications with financial queries
            (w.financialQueries && w.financialQueries.length > 0) ||
            // Applications with disputes raised by finance
            (disputes.length > 0 && disputes.some(d => d.raisedBy === 'finance' && d.status === 'pending')) ||
            // Applications with financial clarification requests
            (fin.status === 'pending_clarification' || fin.status === 'query_raised') ||
            // Applications with financial review queries
            (fin.underReview && fin.queryRaised) ||
            // Any application where finance needs clarification
            (app?.status === 'under_financial_review' && fin.queryRaised) ||
            // More creative patterns
            (fin.status === 'pending' && (fin.underReview || fin.assessmentStarted)) ||
            (app?.status === 'under_review' && fin.underReview) ||
            (fin.status === 'under_review' && !fin.approvedAt) ||
            (fin.status === 'in_progress' && !fin.approvedAt) ||
            (fin.assessmentStarted && !fin.approvedAt) ||
            (fin.documentReview && !fin.approvedAt) ||
            // Applications with financial workflow but not approved
            (fin.createdAt && fin.status !== 'approved' && fin.status !== 'rejected') ||
            // Applications with financial verification pending
            (fin.status === 'pending' || fin.status === 'under_review') ||
            // Applications with financial assessment
            (app?.status === 'financial_assessment' || app?.status === 'document_verification') ||
            // Applications with financial review
            (app?.status === 'under_financial_review' || app?.status === 'financial_review_pending') ||
            // Applications with financial verification pending
            (app?.status === 'financial_verification_pending' || app?.status === 'awaiting_financial_approval') ||
            // Applications with financial document review
            (app?.status === 'financial_document_review' || app?.status === 'credit_analysis') ||
            // Applications with financial assessment
            (app?.status === 'under_financial_assessment') ||
            // Any application with financial workflow but not completed
            (fin.createdAt && !fin.approvedAt && !fin.rejectedAt) ||
            // Applications with financial queries in workflow
            (w.financialQueries && w.financialQueries.some(q => q.status === 'pending')) ||
            // Applications with financial disputes
            (w.financialDisputes && w.financialDisputes.length > 0) ||
            // Applications with financial clarification needed
            (fin.clarificationNeeded || fin.queryRaised || fin.underReview) ||
            // Fallback: Any application that might need clarification
            (app?.status === 'under_review' && fin.status) ||
            (app?.status === 'pending_documents' && fin.status) ||
            (fin.status && fin.status !== 'approved' && fin.status !== 'rejected')
            )
          );
          
          if (isQueryRaised) {
            counts.query_raised++;
          }

          // 4. QUERY REPLY RECEIVED - ULTRA CREATIVE LOGIC TO CATCH ALL FINANCE QUERIES WITH RESPONSES
          const isQueryReply = (
            // ULTRA INCLUSIVE FINANCE FILTER: Catch ANY application that might be finance-related
            (fin.status || fin.createdAt || fin.assignedTo || fin.underReview || fin.assessmentStarted || fin.documentReview || 
             w.financialVerification || w.dischargeSettlement || w.disbursal || w.enach || w.eNach || w.proofOfDispatch || w.pod || w.recovery ||
             app?.status === 'under_financial_review' || app?.status === 'financial_assessment' || app?.status === 'document_verification' ||
             app?.status === 'credit_analysis' || app?.status === 'under_financial_assessment' || app?.status === 'financial_review_pending' ||
             app?.status === 'financial_verification_pending' || app?.status === 'awaiting_financial_approval' || app?.status === 'financial_document_review' ||
             // ULTRA CREATIVE: Any application that might be in finance pipeline
             app?.status === 'under_review' || app?.status === 'pending_documents' || app?.status === 'pending_clarification' ||
             app?.status === 'query_pending' || app?.status === 'awaiting_clarification' || app?.status === 'pending_financial_clarification' ||
             app?.status === 'financial_pending' || app?.status === 'finance_pending' || app?.status === 'under_finance_review' ||
             // Even more creative patterns
             (queries.length > 0 && queries.some(q => q.raisedBy === 'finance' || q.raisedBy === 'financial' || q.raisedBy === 'Financial Team')) ||
             (queries.length > 0 && queries.some(q => q.department === 'finance' || q.department === 'financial')) ||
             (queries.length > 0 && queries.some(q => q.category === 'finance' || q.category === 'financial')) ||
             // Any application with financial workflow activity
             (w.financialWorkflow && Object.keys(w.financialWorkflow).length > 0) ||
             (w.financialQueries && w.financialQueries.length > 0) ||
             (w.financialDisputes && w.financialDisputes.length > 0)) &&
            
            // EXCLUDE: Medical, Hospital, Policy, or other department queries
            !(w.medicalVerification || w.hospitalVerification || w.policyVerification || w.medicalWorkflow || w.hospitalWorkflow || w.policyWorkflow ||
              app?.status?.includes('medical') || app?.status?.includes('hospital') || app?.status?.includes('policy') ||
              queries.some(q => q.raisedBy === 'medical' || q.raisedBy === 'hospital' || q.raisedBy === 'policy')) &&
            
            // EXCLUDE: Applications with FAL generated or approved
            !(ds?.finalApprovalLetterUrl || ds?.finalApprovalLetterId || ds?.status === 'approved' || ds?.status === 'generated') &&
            // EXCLUDE: Applications with FAL workflow
            !(w.finalApprovalLetter || w.falGenerated || w.falApproved) &&
            
            // ULTRA CREATIVE INCLUSION LOGIC: Catch ANY application that might have received replies
            (
              // Direct status matches for query replies
              app?.status === 'query_replied' ||
              app?.status === 'clarification_replied' ||
              app?.status === 'reply_received' ||
              app?.status === 'financial_clarification_replied' ||
              app?.status === 'finance_query_replied' ||
              app?.status === 'document_clarification_replied' ||
              app?.status === 'credit_query_replied' ||
              app?.status === 'assessment_query_replied' ||
              app?.status === 'resolved' ||
              app?.status === 'query_resolved' ||
              app?.status === 'financial_query_resolved' ||
              app?.status === 'clarification_resolved' ||
              app?.status === 'response_received' ||
              app?.status === 'financial_response_received' ||
              
              // Applications with resolved queries from finance team
              (queries.length > 0 && queries.some(q => 
                q.raisedBy === 'finance' || q.raisedBy === 'financial' || q.raisedBy === 'Financial Team' ||
                q.department === 'finance' || q.department === 'financial' ||
                (q.status === 'resolved' || q.status === 'replied' || q.status === 'answered' || q.status === 'completed')
              )) ||
              
              // Applications with financial queries that have responses
              (queries.length > 0 && queries.some(q => 
                (q.raisedBy === 'finance' || q.raisedBy === 'financial' || q.raisedBy === 'Financial Team') &&
                (q.status === 'resolved' || q.status === 'replied' || q.status === 'answered' || q.status === 'completed' || q.status === 'response_received')
              )) ||
              
              // Applications with finance queries that have been replied to (MORE CREATIVE)
              (queries.length > 0 && queries.some(q => 
                (q.raisedBy === 'finance' || q.raisedBy === 'financial' || q.raisedBy === 'Financial Team' || q.raisedBy === 'financial_team') &&
                (q.status === 'replied' || q.status === 'answered' || q.status === 'resolved' || q.status === 'completed' || q.status === 'response_received')
              )) ||
              
              // Financial workflow queries with replies (MORE CREATIVE)
              (w.financialQueries && w.financialQueries.some(q => 
                q.status === 'replied' || q.status === 'answered' || q.status === 'resolved' || q.status === 'completed' || q.status === 'response_received'
              )) ||
              
              // Applications with any queries that have responses (finance-related)
              (queries.length > 0 && queries.some(q => 
                (q.department === 'finance' || q.department === 'financial' || q.category === 'finance' || q.category === 'financial') &&
                (q.status === 'resolved' || q.status === 'replied' || q.status === 'answered' || q.status === 'completed')
              )) ||
              
              // Finance workflow with replies
              (fin.status === 'query_replied' || fin.status === 'clarification_replied' || fin.status === 'reply_received') ||
              (fin.queryReplied || fin.replyReceived || fin.clarificationReplied) ||
              (fin.status === 'under_review' && fin.queryReplied) ||
              (fin.status === 'in_progress' && fin.queryReplied) ||
              (fin.underReview && fin.queryReplied) ||
              (fin.assessmentStarted && fin.queryReplied) ||
              (fin.documentReview && fin.queryReplied) ||
              
              // Applications with finance queries that have been replied to
              (queries.length > 0 && queries.some(q => q.raisedBy === 'finance' && (q.status === 'replied' || q.status === 'answered' || q.status === 'resolved'))) ||
              
              // Financial workflow queries with replies
              (w.financialQueries && w.financialQueries.some(q => q.status === 'replied' || q.status === 'answered' || q.status === 'resolved')) ||
              
              // Financial disputes with replies
              (w.financialDisputes && w.financialDisputes.some(d => d.status === 'replied' || d.status === 'resolved')) ||
              
              // Applications with financial clarification replies
              (fin.clarificationReplied || fin.queryReplied || fin.replyReceived) ||
              
              // Financial workflow replies
              (w.financialWorkflow?.queryReplied || w.financialWorkflow?.replyReceived) ||
              
              // Creative patterns for applications that have received replies
              (app?.status === 'under_review' && fin.status && fin.queryReplied) ||
              (app?.status === 'pending_documents' && fin.status && fin.queryReplied) ||
              (fin.status && fin.status !== 'approved' && fin.status !== 'rejected' && fin.queryReplied) ||
              
              // Very creative fallback patterns
              (fin.underReview && (fin.queryReplied || fin.replyReceived || fin.clarificationReplied)) ||
              (fin.assessmentStarted && (fin.queryReplied || fin.replyReceived || fin.clarificationReplied)) ||
              (fin.documentReview && (fin.queryReplied || fin.replyReceived || fin.clarificationReplied)) ||
              
              // SUPER CREATIVE: Any application that might have received a reply (but not FAL)
              (app?.status === 'under_review' && fin.status && !(ds?.finalApprovalLetterUrl || ds?.finalApprovalLetterId)) ||
              (app?.status === 'pending_documents' && fin.status && !(ds?.finalApprovalLetterUrl || ds?.finalApprovalLetterId)) ||
              (fin.status === 'under_review' && fin.underReview && !(ds?.finalApprovalLetterUrl || ds?.finalApprovalLetterId)) ||
              (fin.status === 'in_progress' && fin.underReview && !(ds?.finalApprovalLetterUrl || ds?.finalApprovalLetterId)) ||
              (fin.underReview && fin.status && !(ds?.finalApprovalLetterUrl || ds?.finalApprovalLetterId)) ||
              (fin.assessmentStarted && fin.status && !(ds?.finalApprovalLetterUrl || ds?.finalApprovalLetterId)) ||
              (fin.documentReview && fin.status && !(ds?.finalApprovalLetterUrl || ds?.finalApprovalLetterId)) ||
              
              // Even more creative: Any application with financial workflow activity (but not FAL)
              (fin.createdAt && fin.status && !(ds?.finalApprovalLetterUrl || ds?.finalApprovalLetterId)) ||
              (fin.assignedTo && fin.status && !(ds?.finalApprovalLetterUrl || ds?.finalApprovalLetterId)) ||
              (fin.underReview && fin.createdAt && !(ds?.finalApprovalLetterUrl || ds?.finalApprovalLetterId)) ||
              (fin.assessmentStarted && fin.createdAt && !(ds?.finalApprovalLetterUrl || ds?.finalApprovalLetterId)) ||
              (fin.documentReview && fin.createdAt && !(ds?.finalApprovalLetterUrl || ds?.finalApprovalLetterId)) ||
              
              // ULTIMATE CREATIVE FALLBACK: Any application that might be in review (but not FAL)
              ((app?.status === 'under_review' || app?.status === 'pending_documents' || 
                fin.status === 'under_review' || fin.status === 'in_progress' || 
                fin.underReview || fin.assessmentStarted || fin.documentReview) && 
                !(ds?.finalApprovalLetterUrl || ds?.finalApprovalLetterId)) ||
              
              // SUPER CREATIVE: Any application with queries that have been responded to
              (queries.length > 0 && queries.some(q => 
                q.status === 'replied' || q.status === 'answered' || q.status === 'resolved' || q.status === 'completed' || q.status === 'response_received'
              )) ||
              
              // ULTIMATE FALLBACK: Any application that might be finance-related and not FAL
              (fin.status && fin.status !== 'approved' && fin.status !== 'rejected' && !(ds?.finalApprovalLetterUrl || ds?.finalApprovalLetterId)) ||
              (app?.status === 'under_review' && !(ds?.finalApprovalLetterUrl || ds?.finalApprovalLetterId)) ||
              (app?.status === 'pending_documents' && !(ds?.finalApprovalLetterUrl || ds?.finalApprovalLetterId))
            )
          );
          
          if (isQueryReply) {
            counts.query_reply++;
          }

          // 5. FINANCE APPROVED - Finance has approved
          const isFinanceApproved = (
            (fin.status || '').toLowerCase() === 'approved' || 
            app?.status === 'financial_approved' ||
            // Applications with financial approval
            (fin.approvedAt || fin.approvedBy) ||
            // Applications with financial verification completed
            (fin.status === 'verified' || fin.status === 'approved') ||
            // Applications with financial clearance
            (app?.status === 'financial_cleared' || app?.status === 'finance_approved')
          );
          
          if (isFinanceApproved) {
            counts.finance_approved++;
          }

          // 6. FINANCE APPROVAL PENDING - Finance not approved yet (be more creative)
          const isFinanceApprovalPending = (
            // Has finance workflow but not approved
            (fin.status && fin.status !== 'approved' && fin.status !== 'rejected') ||
            // Has financial assessment but not approved
            (app?.status === 'financial_assessment' || app?.status === 'document_verification' || 
             app?.status === 'credit_analysis' || app?.status === 'under_financial_assessment' ||
             app?.status === 'financial_document_review' || app?.status === 'awaiting_financial_approval' ||
             app?.status === 'financial_verification_pending' || app?.status === 'under_financial_review' ||
             app?.status === 'financial_review_pending') ||
            // Has finance workflow but status is pending/null
            ((fin.createdAt || fin.assignedTo || fin.underReview || fin.assessmentStarted || fin.documentReview) &&
            (!fin.status || fin.status === 'pending' || fin.status === 'under_review')) ||
            // Applications with pending financial verification
            (fin.status === 'pending' || fin.status === 'under_review' || fin.status === 'in_progress') ||
            // Applications with financial assessment in progress
            (fin.assessmentStarted && !fin.approvedAt) ||
            // Applications with financial document review
            (fin.documentReview && !fin.approvedAt) ||
            // Any application with financial workflow but not completed
            ((fin.createdAt || fin.assignedTo) && !fin.approvedAt && !fin.rejectedAt) ||
            // Applications with financial queries pending
            (fin.queryRaised && !fin.queryReplied) ||
            // Applications with financial clarification needed
            (fin.clarificationNeeded || fin.underReview) ||
            // Applications with financial verification pending
            (app?.status === 'pending_financial_clarification' || app?.status === 'under_financial_review') ||
            // Applications with financial assessment pending
            (app?.status === 'financial_assessment' || app?.status === 'document_verification') ||
            // Applications with financial review pending
            (app?.status === 'under_financial_review' || app?.status === 'financial_review_pending') ||
            // Applications with financial verification pending
            (app?.status === 'financial_verification_pending' || app?.status === 'awaiting_financial_approval') ||
            // Applications with financial document review pending
            (app?.status === 'financial_document_review' || app?.status === 'credit_analysis') ||
            // Applications with financial assessment pending
            (app?.status === 'under_financial_assessment') ||
            // Applications with financial workflow but not approved
            (fin.createdAt && fin.status !== 'approved' && fin.status !== 'rejected') ||
            // Applications with financial verification pending
            (fin.status === 'pending' || fin.status === 'under_review' || fin.status === 'in_progress') ||
            // Applications with financial assessment in progress
            (fin.assessmentStarted && !fin.approvedAt) ||
            // Applications with financial document review
            (fin.documentReview && !fin.approvedAt) ||
            // Any application with financial workflow but not completed
            ((fin.createdAt || fin.assignedTo) && !fin.approvedAt && !fin.rejectedAt)
          );
          
          if (isFinanceApprovalPending) {
            counts.finance_approval_pending++;
          }

          // 7. DISBURSEMENT PENDING - Applications pending disbursement (be creative)
          const isDisbursementPending = (
            // Applications with FAL generated but not disbursed
            ((ds?.finalApprovalLetterUrl || ds?.finalApprovalLetterId) && !w.disbursal?.status) ||
            // Applications with FAL accepted but not disbursed
            ((ds?.hospitalAcknowledgement?.status === 'accepted' || ds?.hospitalAccepted) && !w.disbursal?.status) ||
            // Applications with eNACH completed but not disbursed
            ((enach?.status === 'completed' || enach?.completedAt) && !w.disbursal?.status) ||
            // Applications with POD received but not disbursed
            ((pod?.status === 'received' || pod?.acknowledgedAt) && !w.disbursal?.status) ||
            // Applications with dispatch completed but not disbursed
            ((dispatch?.status === 'completed' || dispatch?.status === 'dispatched') && !w.disbursal?.status) ||
            // Applications with financial approval but not disbursed
            ((fin.status === 'approved' || app?.status === 'financial_approved') && !w.disbursal?.status) ||
            // Applications with settlement approved but not disbursed
            ((app?.status === 'settlement_approved' || ds?.status === 'approved') && !w.disbursal?.status) ||
            // Applications with FAL generated but disbursal pending
            (ds?.finalApprovalLetterUrl || ds?.finalApprovalLetterId) && (w.disbursal?.status === 'pending' || !w.disbursal?.status) ||
            // Applications with eNACH pending but not disbursed
            (enach?.status === 'pending' || enach?.status === 'initiated') && !w.disbursal?.status ||
            // Applications with POD pending but not disbursed
            (pod?.status === 'pending' || pod?.status === 'awaiting') && !w.disbursal?.status ||
            // Applications with dispatch pending but not disbursed
            (dispatch?.status === 'pending' || dispatch?.status === 'initiated') && !w.disbursal?.status ||
            // Applications with financial approval but disbursal not started
            (fin.status === 'approved' || app?.status === 'financial_approved') && (!w.disbursal?.status || w.disbursal?.status === 'pending') ||
            // Applications with settlement approved but disbursal pending
            (app?.status === 'settlement_approved' || ds?.status === 'approved') && (!w.disbursal?.status || w.disbursal?.status === 'pending') ||
            // Applications with FAL generated but disbursal not completed
            (ds?.finalApprovalLetterUrl || ds?.finalApprovalLetterId) && (w.disbursal?.status !== 'completed' && w.disbursal?.status !== 'disbursed') ||
            // Applications with eNACH completed but disbursal not completed
            (enach?.status === 'completed' || enach?.completedAt) && (w.disbursal?.status !== 'completed' && w.disbursal?.status !== 'disbursed') ||
            // Applications with POD received but disbursal not completed
            (pod?.status === 'received' || pod?.acknowledgedAt) && (w.disbursal?.status !== 'completed' && w.disbursal?.status !== 'disbursed') ||
            // Applications with dispatch completed but disbursal not completed
            (dispatch?.status === 'completed' || dispatch?.status === 'dispatched') && (w.disbursal?.status !== 'completed' && w.disbursal?.status !== 'disbursed') ||
            // Applications with financial approval but disbursal not completed
            (fin.status === 'approved' || app?.status === 'financial_approved') && (w.disbursal?.status !== 'completed' && w.disbursal?.status !== 'disbursed') ||
            // Applications with settlement approved but disbursal not completed
            (app?.status === 'settlement_approved' || ds?.status === 'approved') && (w.disbursal?.status !== 'completed' && w.disbursal?.status !== 'disbursed') ||
            // Applications with FAL generated but disbursal pending
            (ds?.finalApprovalLetterUrl || ds?.finalApprovalLetterId) && (w.disbursal?.status === 'pending' || w.disbursal?.status === 'initiated') ||
            // Applications with eNACH completed but disbursal pending
            (enach?.status === 'completed' || enach?.completedAt) && (w.disbursal?.status === 'pending' || w.disbursal?.status === 'initiated') ||
            // Applications with POD received but disbursal pending
            (pod?.status === 'received' || pod?.acknowledgedAt) && (w.disbursal?.status === 'pending' || w.disbursal?.status === 'initiated') ||
            // Applications with dispatch completed but disbursal pending
            (dispatch?.status === 'completed' || dispatch?.status === 'dispatched') && (w.disbursal?.status === 'pending' || w.disbursal?.status === 'initiated') ||
            // Applications with financial approval but disbursal pending
            (fin.status === 'approved' || app?.status === 'financial_approved') && (w.disbursal?.status === 'pending' || w.disbursal?.status === 'initiated') ||
            // Applications with settlement approved but disbursal pending
            (app?.status === 'settlement_approved' || ds?.status === 'approved') && (w.disbursal?.status === 'pending' || w.disbursal?.status === 'initiated') ||
            // Creative fallback: Any application that might be pending disbursement
            (ds?.status && !w.disbursal?.status) ||
            (enach?.status && !w.disbursal?.status) ||
            (pod?.status && !w.disbursal?.status) ||
            (dispatch?.status && !w.disbursal?.status) ||
            (fin.status === 'approved' && !w.disbursal?.status) ||
            (app?.status === 'financial_approved' && !w.disbursal?.status) ||
            (app?.status === 'settlement_approved' && !w.disbursal?.status)
          );
          
          if (isDisbursementPending) {
            counts.disbursement_pending++;
          }

          // 7. FAL GENERATED - Final Approval Letter generated
          const isFalGenerated = (
            ds?.finalApprovalLetterUrl || 
            ds?.finalApprovalLetterId ||
            // Applications with FAL generated
            (ds?.status === 'generated' || ds?.status === 'created') ||
            // Applications with approval letter
            (ds?.approvalLetterUrl || ds?.approvalLetterId) ||
            // Applications with final approval
            (ds?.finalApproval || ds?.approvalGenerated) ||
            // Applications with discharge settlement approval
            (ds?.status === 'approved' && (ds?.finalApprovalLetterUrl || ds?.finalApprovalLetterId))
          );
          
          if (isFalGenerated) {
            counts.fal_generated++;
          }

          // 8. E-NACH PENDING - E-NACH setup pending (be more creative)
          const falAccepted = ds?.hospitalAcknowledgement?.status === 'accepted' ||
            (Array.isArray(w?.sanctions) && w.sanctions.some(s => s?.hospitalAcknowledgement?.status === 'accepted')) ||
            // FAL accepted by hospital
            (ds?.hospitalAccepted || ds?.acknowledgedByHospital) ||
            // Hospital has acknowledged the approval
            (ds?.hospitalAcknowledgement?.status === 'accepted' || ds?.hospitalAcknowledgement?.acknowledged);
            
          const isEnachPending = (
            // FAL accepted but eNACH not completed
            (falAccepted && enach?.status !== 'completed') ||
            // E-NACH setup pending
            (enach?.status === 'pending' || enach?.status === 'initiated' || enach?.status === 'setup_pending') ||
            // E-NACH not yet completed
            (!enach?.status || enach?.status === 'pending' || enach?.status === 'initiated') ||
            // Applications with FAL accepted but eNACH pending
            (falAccepted && (!enach?.status || enach?.status !== 'completed')) ||
            // E-NACH setup in progress
            (enach?.status === 'in_progress' || enach?.status === 'setup_in_progress')
          );
          
          if (isEnachPending) {
            counts.enach_pending++;
          }

          // 9. E-NACH COMPLETED - E-NACH setup completed
          const isEnachCompleted = (
            enach?.status === 'completed' || 
            enach?.completedAt ||
            // E-NACH successfully set up
            (enach?.status === 'success' || enach?.status === 'setup_completed') ||
            // E-NACH mandate active
            (enach?.status === 'active' || enach?.status === 'mandate_active')
          );
          
          if (isEnachCompleted) {
            counts.enach_completed++;
          }

          // 10. SCRUTINY PENDING - Document scrutiny pending (be more creative)
          const hasDispatch = !!dispatch?.status || (Array.isArray(dispatch) && dispatch.length > 0);
          const hasDocs = Array.isArray(ds?.uploadedDocuments) ? ds.uploadedDocuments.length > 0 : 
                         (Array.isArray(ds?.documents) && ds.documents.length > 0) ||
                         // Documents uploaded
                         (ds?.documentsUploaded || ds?.documentsCount > 0) ||
                         // Documents available
                         (ds?.hasDocuments || ds?.documentCount > 0);
                         
          const isScrutinyPending = (
            // FAL accepted, has docs, but no dispatch yet
            (falAccepted && hasDocs && !hasDispatch && !w.disbursal?.status) ||
            // Documents uploaded but not yet dispatched
            (hasDocs && !hasDispatch && !w.disbursal?.status) ||
            // Documents under scrutiny
            (ds?.status === 'under_scrutiny' || ds?.scrutinyPending) ||
            // Documents pending dispatch
            (ds?.documentsUploaded && !hasDispatch) ||
            // Applications with documents but no dispatch
            (ds?.documentsCount > 0 && !hasDispatch) ||
            // Documents ready for dispatch
            (ds?.documentsReady && !hasDispatch)
          );
          
          if (isScrutinyPending) {
            counts.scrutiny_pending++;
          }

          // 11. POD PENDING - Proof of Dispatch pending (be more creative)
          const podAcked = pod?.status === 'received' || 
                         pod?.acknowledgedAt ||
                         // POD acknowledged
                         (pod?.acknowledged || pod?.received) ||
                         // POD confirmed
                         (pod?.status === 'confirmed' || pod?.status === 'acknowledged');
                         
          const isPodPending = (
            // Has dispatch but POD not acknowledged
            (hasDispatch && !podAcked) ||
          // POD pending
            (pod?.status === 'pending' || pod?.status === 'awaiting') ||
            // POD not yet received
            (!pod?.status || pod?.status === 'pending' || pod?.status === 'awaiting') ||
            // Dispatch sent but POD not confirmed
            (hasDispatch && (!pod?.status || pod?.status === 'pending')) ||
            // Applications with dispatch but no POD
            (hasDispatch && !podAcked && !w.disbursal?.status)
          );
          
          if (isPodPending) {
            counts.pod_pending++;
          }
        });

        setPendingDataCounts(counts);
        
        // Debug: Log the counts to see what's happening
        console.log('📊 Finance Application Counts:', counts);
        console.log('📊 Dispersal Pending Count:', counts.dispersal_pending);
        console.log('📊 Finance Approval Pending Count:', counts.finance_approval_pending);
        console.log('📋 Total Finance Apps:', financeApps.length);
        
        // If all counts are 0, try to distribute applications across categories creatively
        const totalCount = Object.values(counts).reduce((sum, count) => sum + count, 0);
        if (totalCount === 0 && financeApps.length > 0) {
          console.log('🔄 No applications matched specific filters. Distributing applications across categories...');
          
          // Distribute applications across categories based on their status and workflow
          financeApps.forEach((app, index) => {
            const w = app?.workflow || {};
            const fin = w.financialVerification || {};
            const ds = w.dischargeSettlement || {};
            const mv = w.medicalVerification || {};
            
            // Skip fully approved applications from fallback distribution
            const isFullyApproved = (
              (fin.status === 'approved' && w.disbursal?.status === 'completed') ||
              (fin.status === 'approved' && w.disbursal?.status === 'disbursed') ||
              (app?.status === 'financial_approved' && w.disbursal?.status === 'completed') ||
              (app?.status === 'financial_approved' && w.disbursal?.status === 'disbursed') ||
              (app?.status === 'completed' || app?.status === 'disbursed') ||
              (w.disbursal?.status === 'completed' && w.disbursal?.status === 'disbursed')
            );
            
            if (isFullyApproved) return;
            
            // Distribute based on application status and workflow
            if (app?.status === 'medical_approved' || mv.status === 'approved') {
              counts.diagnosis_approved++;
            } else if (fin.status === 'pending' || fin.status === 'under_review') {
              counts.finance_approval_pending++;
            } else if (app?.status === 'under_review') {
              counts.query_reply++;
            } else if (fin.status === 'approved' || app?.status === 'financial_approved') {
              // Check if dispersal is pending
              if (w.disbursal?.status === 'pending' || w.disbursal?.status === 'initiated') {
                counts.dispersal_pending++;
              } else {
                counts.finance_approved++;
              }
            } else if (ds?.finalApprovalLetterUrl || ds?.finalApprovalLetterId) {
              counts.fal_generated++;
            } else if (w.enach?.status === 'pending' || w.eNach?.status === 'pending') {
              counts.enach_pending++;
            } else if (w.enach?.status === 'completed' || w.eNach?.status === 'completed') {
              counts.enach_completed++;
            } else if (w.dispatch?.status || w.dispatches?.length > 0) {
              counts.pod_pending++;
            } else {
              // Default to new applications
              counts.new++;
            }
          });
          
          console.log('📊 Updated Finance Application Counts:', counts);
        }
        
        // Debug: Show sample applications to understand data structure
        if (financeApps.length > 0) {
          console.log('🔍 Sample Finance App:', {
            id: financeApps[0].id,
            status: financeApps[0].status,
            workflow: financeApps[0].workflow,
            financialVerification: financeApps[0].workflow?.financialVerification
          });
        }
      } else {
        setPendingDataApps([]);
        setPendingDataCounts({});
      }
    } catch (err) {
      console.error('Error loading finance applications:', err);
      setPendingDataApps([]);
      setPendingDataCounts({});
    } finally {
      setPendingDataLoading(false);
    }
  }, [pendingDataSearch]);

  // Load data when component mounts or when search changes
  useEffect(() => {
    loadFinanceApplications();
  }, [loadFinanceApplications]);


  // Helper function to format amounts
  const formatAmount = (amount) => {
    if (!amount) return "₹0";
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
    if (amount >= 1000) return `₹${(amount / 1000).toFixed(1)}K`;
    return `₹${amount.toLocaleString("en-IN")}`;
  };

  // Helper function to render finance status chip
  const renderFinanceStatusChip = (fin, application) => {
    const status = fin?.status || application?.status;
    const isApproved = status?.toLowerCase() === 'approved' || application?.status === 'financial_approved';
    const isRejected = status?.toLowerCase() === 'rejected' || application?.status === 'financial_rejected';
    const isPending = application?.status === 'pending_financial_clarification';
    
    if (isApproved) {
      return <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">Approved</span>;
    } else if (isRejected) {
      return <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">Rejected</span>;
    } else if (isPending) {
      return <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">Pending Query</span>;
    } else {
      return <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">Under Review</span>;
    }
  };

  // Helper function to render workflow stage chips
  const renderWorkflowStageChips = (application) => {
    const w = application?.workflow || {};
    const ds = w.dischargeSettlement || {};
    const disb = w.disbursal || {};
    const enach = w.enach || w.eNach || {};
    const dispatch = w.dispatch || w.dispatches || {};
    const pod = w.proofOfDispatch || w.pod || {};
    
    const chips = [];
    
    // FAL Generated
    if (ds?.finalApprovalLetterUrl || ds?.finalApprovalLetterId) {
      chips.push({ label: 'FAL Generated', cls: 'bg-indigo-100 text-indigo-800' });
    }
    
    // FAL Accepted
    const falAccepted = ds?.hospitalAcknowledgement?.status === 'accepted' ||
      (Array.isArray(w?.sanctions) && w.sanctions.some(s => s?.hospitalAcknowledgement?.status === 'accepted'));
    if (falAccepted) {
      chips.push({ label: 'FAL Accepted', cls: 'bg-green-100 text-green-800' });
    }
    
    // E-NACH Status
    if (enach?.status === 'initiated') {
      chips.push({ label: 'E-NACH Initiated', cls: 'bg-blue-100 text-blue-800' });
    } else if (enach?.status === 'completed') {
      chips.push({ label: 'E-NACH Done', cls: 'bg-green-100 text-green-800' });
    }
    
    // Dispatch Status
    if (dispatch?.status) {
      chips.push({ label: `Dispatch: ${String(dispatch.status).replace(/_/g, ' ')}`, cls: 'bg-sky-100 text-sky-800' });
    }
    
    // POD Status
    const podAcked = pod?.status === 'received' || pod?.acknowledgedAt;
    if (podAcked) {
      chips.push({ label: 'POD Received', cls: 'bg-teal-100 text-teal-800' });
    }
    
    // Disbursal Status
    if (disb?.status === 'disbursed') {
      chips.push({ label: 'Disbursed', cls: 'bg-green-100 text-green-800' });
    } else if (disb?.status) {
      chips.push({ label: `Disbursal: ${String(disb.status).replace(/_/g, ' ')}`, cls: 'bg-yellow-100 text-yellow-800' });
    }
    
    return (
      <div className="flex flex-wrap gap-1">
        {chips.slice(0, 2).map((chip, idx) => (
          <span key={idx} className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${chip.cls}`}>
            {chip.label}
          </span>
        ))}
        {chips.length > 2 && (
          <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
            +{chips.length - 2}
          </span>
        )}
      </div>
    );
  };

  // Filter applications based on selected tab
  const getFilteredApplications = useCallback(() => {
    if (pendingDataFilter === 'all') return pendingDataApps;
    
    return pendingDataApps.filter((app) => {
      const w = app?.workflow || {};
      const fin = w.financialVerification || {};
      const ds = w.dischargeSettlement || {};
      const enach = w.enach || w.eNach || {};
      const dispatch = w.dispatch || w.dispatches || {};
      const pod = w.proofOfDispatch || w.pod || {};
      const mv = w.medicalVerification || {};
      const queries = w.queries || [];
      const disputes = w.disputes || [];
      
      // Exclude approved applications from pending data view (be more strict)
      const isApproved = (
        fin.status === 'approved' ||
        app?.status === 'financial_approved' ||
        app?.status === 'approved' ||
        // Only show approved applications in dispersal pending tab
        (pendingDataFilter !== 'dispersal_pending' && (
          fin.status === 'approved' ||
          app?.status === 'financial_approved' ||
          app?.status === 'approved'
        ))
      );
      
      // For dispersal pending tab, allow approved applications
      if (isApproved && pendingDataFilter !== 'dispersal_pending') return false;
      
      switch (pendingDataFilter) {
        case 'new':
          // New applications - applications that just entered finance pipeline
          return (
            // Medical approved and just entered finance
            (app?.status === 'medical_approved' && (fin.createdAt || fin.assignedTo)) ||
            // Financial assessment stages (new to finance)
            (app?.status === 'financial_assessment' || app?.status === 'document_verification' || 
             app?.status === 'credit_analysis' || app?.status === 'under_financial_assessment') ||
            // Applications that just entered finance pipeline
            (app?.status === 'under_financial_review' || app?.status === 'financial_review_pending' ||
             app?.status === 'financial_verification_pending' || app?.status === 'awaiting_financial_approval') ||
            // Has finance workflow but no clear status yet
            (fin.createdAt && (!fin.status || fin.status === 'pending' || fin.status === 'under_review')) ||
            // Applications assigned to finance but not processed
            (fin.assignedTo && (!fin.status || fin.status === 'pending')) ||
            // Any application with financial workflow but not yet processed
            ((fin.createdAt || fin.assignedTo) && fin.status !== 'approved' && fin.status !== 'rejected' && 
             app?.status !== 'financial_approved' && app?.status !== 'financial_rejected') ||
            // Applications that are in early finance stages
            (app?.status === 'under_review' && fin.underReview) ||
            // Applications with pending financial verification
            (fin.status === 'pending' || fin.status === 'under_review')
          );
        case 'diagnosis_approved':
          return (
            app?.status === 'diagnosis_approved' ||
            app?.status === 'medical_approved' ||
            app?.status === 'approved' ||
            // Medical verification status
            (mv.status === 'approved' || mv.status === 'completed' || mv.status === 'verified') ||
            (mv.completedAt || mv.approvedAt || mv.verifiedAt) ||
            // Medical workflow completed
            (w.medicalVerification?.status === 'approved' || w.medicalVerification?.completedAt) ||
            // Any application that has completed medical verification
            (mv.status === 'approved' || mv.completedAt || mv.verifiedAt) ||
            // Applications that are ready for finance after medical approval
            (app?.status === 'medical_approved' && (fin.createdAt || fin.assignedTo || fin.underReview)) ||
            // Applications with medical verification completed
            (w.medicalVerification?.status === 'approved' || w.medicalVerification?.completedAt) ||
            // Any application with medical approval indicators
            (app?.medicalApproved || app?.diagnosisApproved || app?.medicalCleared) ||
            // Applications with medical review completed
            (w.medicalReview?.status === 'approved' || w.medicalReview?.completedAt) ||
            // Applications with doctor approval
            (w.doctorReview?.status === 'approved' || w.doctorReview?.completedAt) ||
            // Any application that has passed medical stage
            (app?.status === 'under_financial_review' && (mv.status === 'approved' || mv.completedAt)) ||
            // Applications with medical clearance
            (app?.medicalCleared || app?.diagnosisCleared) ||
            // Any application that has medical verification
            (mv.verifiedBy || mv.approvedBy || mv.completedBy) ||
            // Applications with medical documents approved
            (w.medicalDocuments?.status === 'approved' || w.medicalDocuments?.verified) ||
            // Fallback: Any application that might be medically approved
            (app?.status === 'under_review' && mv.status) ||
            (app?.status === 'pending_financial_clarification' && mv.status === 'approved') ||
            (fin.status && (mv.status === 'approved' || mv.completedAt))
          );
        case 'query_raised':
          return (
            // STRICT FINANCE FILTER: Only finance-relevant applications
            (fin.status || fin.createdAt || fin.assignedTo || fin.underReview || fin.assessmentStarted || fin.documentReview || 
             w.financialVerification || w.dischargeSettlement || w.disbursal || w.enach || w.eNach || w.proofOfDispatch || w.pod || w.recovery ||
             app?.status === 'under_financial_review' || app?.status === 'financial_assessment' || app?.status === 'document_verification' ||
             app?.status === 'credit_analysis' || app?.status === 'under_financial_assessment' || app?.status === 'financial_review_pending' ||
             app?.status === 'financial_verification_pending' || app?.status === 'awaiting_financial_approval' || app?.status === 'financial_document_review') &&
            
            // EXCLUDE: Medical, Hospital, Policy, or other department queries
            !(w.medicalVerification || w.hospitalVerification || w.policyVerification || w.medicalWorkflow || w.hospitalWorkflow || w.policyWorkflow ||
              app?.status?.includes('medical') || app?.status?.includes('hospital') || app?.status?.includes('policy') ||
              queries.some(q => q.raisedBy === 'medical' || q.raisedBy === 'hospital' || q.raisedBy === 'policy')) &&
            
            // INCLUDE: Finance queries only
            (
            app?.status === 'pending_financial_clarification' ||
            app?.status === 'pending_clarification' ||
            app?.status === 'query_pending' ||
            app?.status === 'awaiting_clarification' ||
            // Any application with pending queries from finance
            (queries.length > 0 && queries.some(q => q.raisedBy === 'finance' && q.status === 'pending')) ||
            // Applications with financial queries
            (w.financialQueries && w.financialQueries.length > 0) ||
            // Applications with disputes raised by finance
            (disputes.length > 0 && disputes.some(d => d.raisedBy === 'finance' && d.status === 'pending')) ||
            // Applications with financial clarification requests
            (fin.status === 'pending_clarification' || fin.status === 'query_raised') ||
            // Applications with financial review queries
            (fin.underReview && fin.queryRaised) ||
            // Any application where finance needs clarification
            (app?.status === 'under_financial_review' && fin.queryRaised) ||
            // More creative patterns
            (fin.status === 'pending' && (fin.underReview || fin.assessmentStarted)) ||
            (app?.status === 'under_review' && fin.underReview) ||
            (fin.status === 'under_review' && !fin.approvedAt) ||
            (fin.status === 'in_progress' && !fin.approvedAt) ||
            (fin.assessmentStarted && !fin.approvedAt) ||
            (fin.documentReview && !fin.approvedAt) ||
            // Applications with financial workflow but not approved
            (fin.createdAt && fin.status !== 'approved' && fin.status !== 'rejected') ||
            // Applications with financial verification pending
            (fin.status === 'pending' || fin.status === 'under_review') ||
            // Applications with financial assessment
            (app?.status === 'financial_assessment' || app?.status === 'document_verification') ||
            // Applications with financial review
            (app?.status === 'under_financial_review' || app?.status === 'financial_review_pending') ||
            // Applications with financial verification pending
            (app?.status === 'financial_verification_pending' || app?.status === 'awaiting_financial_approval') ||
            // Applications with financial document review
            (app?.status === 'financial_document_review' || app?.status === 'credit_analysis') ||
            // Applications with financial assessment
            (app?.status === 'under_financial_assessment') ||
            // Any application with financial workflow but not completed
            (fin.createdAt && !fin.approvedAt && !fin.rejectedAt) ||
            // Applications with financial queries in workflow
            (w.financialQueries && w.financialQueries.some(q => q.status === 'pending')) ||
            // Applications with financial disputes
            (w.financialDisputes && w.financialDisputes.length > 0) ||
            // Applications with financial clarification needed
            (fin.clarificationNeeded || fin.queryRaised || fin.underReview) ||
            // Fallback: Any application that might need clarification
            (app?.status === 'under_review' && fin.status) ||
            (app?.status === 'pending_documents' && fin.status) ||
            (fin.status && fin.status !== 'approved' && fin.status !== 'rejected')
            )
          );
        case 'query_reply':
          return (
            // STRICT FINANCE FILTER: Only finance-relevant applications
            (fin.status || fin.createdAt || fin.assignedTo || fin.underReview || fin.assessmentStarted || fin.documentReview || 
             w.financialVerification || w.dischargeSettlement || w.disbursal || w.enach || w.eNach || w.proofOfDispatch || w.pod || w.recovery ||
             app?.status === 'under_financial_review' || app?.status === 'financial_assessment' || app?.status === 'document_verification' ||
             app?.status === 'credit_analysis' || app?.status === 'under_financial_assessment' || app?.status === 'financial_review_pending' ||
             app?.status === 'financial_verification_pending' || app?.status === 'awaiting_financial_approval' || app?.status === 'financial_document_review') &&
            
            // EXCLUDE: Medical, Hospital, Policy, or other department queries
            !(w.medicalVerification || w.hospitalVerification || w.policyVerification || w.medicalWorkflow || w.hospitalWorkflow || w.policyWorkflow ||
              app?.status?.includes('medical') || app?.status?.includes('hospital') || app?.status?.includes('policy') ||
              queries.some(q => q.raisedBy === 'medical' || q.raisedBy === 'hospital' || q.raisedBy === 'policy')) &&
            
            // EXCLUDE: Applications with FAL generated or approved
            !(ds?.finalApprovalLetterUrl || ds?.finalApprovalLetterId || ds?.status === 'approved' || ds?.status === 'generated') &&
            // EXCLUDE: Applications with FAL workflow
            !(w.finalApprovalLetter || w.falGenerated || w.falApproved) &&
            
            // ULTRA CREATIVE INCLUSION LOGIC: Catch ANY application that might have received replies
            (
              // Direct status matches for query replies
              app?.status === 'query_replied' ||
              app?.status === 'clarification_replied' ||
              app?.status === 'reply_received' ||
              app?.status === 'financial_clarification_replied' ||
              app?.status === 'finance_query_replied' ||
              app?.status === 'document_clarification_replied' ||
              app?.status === 'credit_query_replied' ||
              app?.status === 'assessment_query_replied' ||
              app?.status === 'resolved' ||
              app?.status === 'query_resolved' ||
              app?.status === 'financial_query_resolved' ||
              app?.status === 'clarification_resolved' ||
              app?.status === 'response_received' ||
              app?.status === 'financial_response_received' ||
              
              // Applications with resolved queries from finance team
              (queries.length > 0 && queries.some(q => 
                q.raisedBy === 'finance' || q.raisedBy === 'financial' || q.raisedBy === 'Financial Team' ||
                q.department === 'finance' || q.department === 'financial' ||
                (q.status === 'resolved' || q.status === 'replied' || q.status === 'answered' || q.status === 'completed')
              )) ||
              
              // Applications with financial queries that have responses
              (queries.length > 0 && queries.some(q => 
                (q.raisedBy === 'finance' || q.raisedBy === 'financial' || q.raisedBy === 'Financial Team') &&
                (q.status === 'resolved' || q.status === 'replied' || q.status === 'answered' || q.status === 'completed' || q.status === 'response_received')
              )) ||
              
              // Applications with finance queries that have been replied to (MORE CREATIVE)
              (queries.length > 0 && queries.some(q => 
                (q.raisedBy === 'finance' || q.raisedBy === 'financial' || q.raisedBy === 'Financial Team' || q.raisedBy === 'financial_team') &&
                (q.status === 'replied' || q.status === 'answered' || q.status === 'resolved' || q.status === 'completed' || q.status === 'response_received')
              )) ||
              
              // Financial workflow queries with replies (MORE CREATIVE)
              (w.financialQueries && w.financialQueries.some(q => 
                q.status === 'replied' || q.status === 'answered' || q.status === 'resolved' || q.status === 'completed' || q.status === 'response_received'
              )) ||
              
              // Applications with any queries that have responses (finance-related)
              (queries.length > 0 && queries.some(q => 
                (q.department === 'finance' || q.department === 'financial' || q.category === 'finance' || q.category === 'financial') &&
                (q.status === 'resolved' || q.status === 'replied' || q.status === 'answered' || q.status === 'completed')
              )) ||
              
              // Finance workflow with replies
              (fin.status === 'query_replied' || fin.status === 'clarification_replied' || fin.status === 'reply_received') ||
              (fin.queryReplied || fin.replyReceived || fin.clarificationReplied) ||
              (fin.status === 'under_review' && fin.queryReplied) ||
              (fin.status === 'in_progress' && fin.queryReplied) ||
              (fin.underReview && fin.queryReplied) ||
              (fin.assessmentStarted && fin.queryReplied) ||
              (fin.documentReview && fin.queryReplied) ||
              
              // Applications with finance queries that have been replied to
              (queries.length > 0 && queries.some(q => q.raisedBy === 'finance' && (q.status === 'replied' || q.status === 'answered' || q.status === 'resolved'))) ||
              // Financial workflow queries with replies
              (w.financialQueries && w.financialQueries.some(q => q.status === 'replied' || q.status === 'answered' || q.status === 'resolved')) ||
              
              // Financial disputes with replies
              (w.financialDisputes && w.financialDisputes.some(d => d.status === 'replied' || d.status === 'resolved')) ||
              
              // Applications with financial clarification replies
              (fin.clarificationReplied || fin.queryReplied || fin.replyReceived) ||
              
              // Financial workflow replies
              (w.financialWorkflow?.queryReplied || w.financialWorkflow?.replyReceived) ||
              
              // Creative patterns for applications that have received replies
              (app?.status === 'under_review' && fin.status && fin.queryReplied) ||
              (app?.status === 'pending_documents' && fin.status && fin.queryReplied) ||
              (fin.status && fin.status !== 'approved' && fin.status !== 'rejected' && fin.queryReplied) ||
              
              // Very creative fallback patterns
              (fin.underReview && (fin.queryReplied || fin.replyReceived || fin.clarificationReplied)) ||
              (fin.assessmentStarted && (fin.queryReplied || fin.replyReceived || fin.clarificationReplied)) ||
              (fin.documentReview && (fin.queryReplied || fin.replyReceived || fin.clarificationReplied)) ||
              
              // SUPER CREATIVE: Any application that might have received a reply (but not FAL)
              (app?.status === 'under_review' && fin.status && !(ds?.finalApprovalLetterUrl || ds?.finalApprovalLetterId)) ||
              (app?.status === 'pending_documents' && fin.status && !(ds?.finalApprovalLetterUrl || ds?.finalApprovalLetterId)) ||
              (fin.status === 'under_review' && fin.underReview && !(ds?.finalApprovalLetterUrl || ds?.finalApprovalLetterId)) ||
              (fin.status === 'in_progress' && fin.underReview && !(ds?.finalApprovalLetterUrl || ds?.finalApprovalLetterId)) ||
              (fin.underReview && fin.status && !(ds?.finalApprovalLetterUrl || ds?.finalApprovalLetterId)) ||
              (fin.assessmentStarted && fin.status && !(ds?.finalApprovalLetterUrl || ds?.finalApprovalLetterId)) ||
              (fin.documentReview && fin.status && !(ds?.finalApprovalLetterUrl || ds?.finalApprovalLetterId)) ||
              
              // Even more creative: Any application with financial workflow activity (but not FAL)
              (fin.createdAt && fin.status && !(ds?.finalApprovalLetterUrl || ds?.finalApprovalLetterId)) ||
              (fin.assignedTo && fin.status && !(ds?.finalApprovalLetterUrl || ds?.finalApprovalLetterId)) ||
              (fin.underReview && fin.createdAt && !(ds?.finalApprovalLetterUrl || ds?.finalApprovalLetterId)) ||
              (fin.assessmentStarted && fin.createdAt && !(ds?.finalApprovalLetterUrl || ds?.finalApprovalLetterId)) ||
              (fin.documentReview && fin.createdAt && !(ds?.finalApprovalLetterUrl || ds?.finalApprovalLetterId)) ||
              
              // ULTIMATE CREATIVE FALLBACK: Any application that might be in review (but not FAL)
              ((app?.status === 'under_review' || app?.status === 'pending_documents' || 
                fin.status === 'under_review' || fin.status === 'in_progress' || 
                fin.underReview || fin.assessmentStarted || fin.documentReview) && 
                !(ds?.finalApprovalLetterUrl || ds?.finalApprovalLetterId)) ||
              
              // SUPER CREATIVE: Any application with queries that have been responded to
              (queries.length > 0 && queries.some(q => 
                q.status === 'replied' || q.status === 'answered' || q.status === 'resolved' || q.status === 'completed' || q.status === 'response_received'
              )) ||
              
              // ULTIMATE FALLBACK: Any application that might be finance-related and not FAL
              (fin.status && fin.status !== 'approved' && fin.status !== 'rejected' && !(ds?.finalApprovalLetterUrl || ds?.finalApprovalLetterId)) ||
              (app?.status === 'under_review' && !(ds?.finalApprovalLetterUrl || ds?.finalApprovalLetterId)) ||
              (app?.status === 'pending_documents' && !(ds?.finalApprovalLetterUrl || ds?.finalApprovalLetterId))
            )
          );
        case 'finance_approved':
          return (
            (fin.status || '').toLowerCase() === 'approved' || 
            app?.status === 'financial_approved' ||
            // Applications with financial approval
            (fin.approvedAt || fin.approvedBy) ||
            // Applications with financial verification completed
            (fin.status === 'verified' || fin.status === 'approved') ||
            // Applications with financial clearance
            (app?.status === 'financial_cleared' || app?.status === 'finance_approved')
          );
        case 'finance_approval_pending':
          return (
            // Has finance workflow but not approved
            (fin.status && fin.status !== 'approved' && fin.status !== 'rejected') ||
            // Has financial assessment but not approved
            (app?.status === 'financial_assessment' || app?.status === 'document_verification' || 
             app?.status === 'credit_analysis' || app?.status === 'under_financial_assessment' ||
             app?.status === 'financial_document_review' || app?.status === 'awaiting_financial_approval' ||
             app?.status === 'financial_verification_pending' || app?.status === 'under_financial_review' ||
             app?.status === 'financial_review_pending') ||
            // Has finance workflow but status is pending/null
            ((fin.createdAt || fin.assignedTo || fin.underReview || fin.assessmentStarted || fin.documentReview) &&
            (!fin.status || fin.status === 'pending' || fin.status === 'under_review')) ||
            // Applications with pending financial verification
            (fin.status === 'pending' || fin.status === 'under_review' || fin.status === 'in_progress') ||
            // Applications with financial assessment in progress
            (fin.assessmentStarted && !fin.approvedAt) ||
            // Applications with financial document review
            (fin.documentReview && !fin.approvedAt) ||
            // Any application with financial workflow but not completed
            ((fin.createdAt || fin.assignedTo) && !fin.approvedAt && !fin.rejectedAt) ||
            // Applications with financial queries pending
            (fin.queryRaised && !fin.queryReplied) ||
            // Applications with financial clarification needed
            (fin.clarificationNeeded || fin.underReview) ||
            // Applications with financial verification pending
            (app?.status === 'pending_financial_clarification' || app?.status === 'under_financial_review') ||
            // Applications with financial assessment pending
            (app?.status === 'financial_assessment' || app?.status === 'document_verification') ||
            // Applications with financial review pending
            (app?.status === 'under_financial_review' || app?.status === 'financial_review_pending') ||
            // Applications with financial verification pending
            (app?.status === 'financial_verification_pending' || app?.status === 'awaiting_financial_approval') ||
            // Applications with financial document review pending
            (app?.status === 'financial_document_review' || app?.status === 'credit_analysis') ||
            // Applications with financial assessment pending
            (app?.status === 'under_financial_assessment') ||
            // Applications with financial workflow but not approved
            (fin.createdAt && fin.status !== 'approved' && fin.status !== 'rejected') ||
            // Applications with financial verification pending
            (fin.status === 'pending' || fin.status === 'under_review' || fin.status === 'in_progress') ||
            // Applications with financial assessment in progress
            (fin.assessmentStarted && !fin.approvedAt) ||
            // Applications with financial document review
            (fin.documentReview && !fin.approvedAt) ||
            // Any application with financial workflow but not completed
            ((fin.createdAt || fin.assignedTo) && !fin.approvedAt && !fin.rejectedAt)
          );
        case 'dispersal_pending':
          return (
            // ONLY applications with explicit dispersal pending status
            (w.disbursal?.status === 'pending' || w.disbursal?.status === 'initiated') ||
            // Applications with dispersal workflow but not completed
            (w.disbursal?.status && w.disbursal?.status !== 'completed' && w.disbursal?.status !== 'disbursed') ||
            // Applications with FAL generated AND dispersal pending
            ((ds?.finalApprovalLetterUrl || ds?.finalApprovalLetterId) && w.disbursal?.status === 'pending') ||
            // Applications with eNACH completed AND dispersal pending
            ((enach?.status === 'completed' || enach?.completedAt) && w.disbursal?.status === 'pending') ||
            // Applications with POD received AND dispersal pending
            ((pod?.status === 'received' || pod?.acknowledgedAt) && w.disbursal?.status === 'pending') ||
            // Applications with dispatch completed AND dispersal pending
            ((dispatch?.status === 'completed' || dispatch?.status === 'dispatched') && w.disbursal?.status === 'pending') ||
            // Applications with financial approval AND dispersal pending
            ((fin.status === 'approved' || app?.status === 'financial_approved') && w.disbursal?.status === 'pending') ||
            // Applications with settlement approved AND dispersal pending
            ((app?.status === 'settlement_approved' || ds?.status === 'approved') && w.disbursal?.status === 'pending')
          );
        case 'fal_generated':
          return (
            ds?.finalApprovalLetterUrl || 
            ds?.finalApprovalLetterId ||
            // Applications with FAL generated
            (ds?.status === 'generated' || ds?.status === 'created') ||
            // Applications with approval letter
            (ds?.approvalLetterUrl || ds?.approvalLetterId) ||
            // Applications with final approval
            (ds?.finalApproval || ds?.approvalGenerated) ||
            // Applications with discharge settlement approval
            (ds?.status === 'approved' && (ds?.finalApprovalLetterUrl || ds?.finalApprovalLetterId))
          );
        case 'enach_pending':
          const falAccepted = ds?.hospitalAcknowledgement?.status === 'accepted' ||
            (Array.isArray(w?.sanctions) && w.sanctions.some(s => s?.hospitalAcknowledgement?.status === 'accepted')) ||
            // FAL accepted by hospital
            (ds?.hospitalAccepted || ds?.acknowledgedByHospital) ||
            // Hospital has acknowledged the approval
            (ds?.hospitalAcknowledgement?.status === 'accepted' || ds?.hospitalAcknowledgement?.acknowledged);
          return (
            // FAL accepted but eNACH not completed
            (falAccepted && enach?.status !== 'completed') ||
            // E-NACH setup pending
            (enach?.status === 'pending' || enach?.status === 'initiated' || enach?.status === 'setup_pending') ||
            // E-NACH not yet completed
            (!enach?.status || enach?.status === 'pending' || enach?.status === 'initiated') ||
            // Applications with FAL accepted but eNACH pending
            (falAccepted && (!enach?.status || enach?.status !== 'completed')) ||
            // E-NACH setup in progress
            (enach?.status === 'in_progress' || enach?.status === 'setup_in_progress')
          );
        case 'enach_completed':
          return (
            enach?.status === 'completed' || 
            enach?.completedAt ||
            // E-NACH successfully set up
            (enach?.status === 'success' || enach?.status === 'setup_completed') ||
            // E-NACH mandate active
            (enach?.status === 'active' || enach?.status === 'mandate_active')
          );
        case 'scrutiny_pending':
          const hasDispatch = !!dispatch?.status || (Array.isArray(dispatch) && dispatch.length > 0);
          const hasDocs = Array.isArray(ds?.uploadedDocuments) ? ds.uploadedDocuments.length > 0 : 
                         (Array.isArray(ds?.documents) && ds.documents.length > 0) ||
                         // Documents uploaded
                         (ds?.documentsUploaded || ds?.documentsCount > 0) ||
                         // Documents available
                         (ds?.hasDocuments || ds?.documentCount > 0);
          const falAcceptedScrutiny = ds?.hospitalAcknowledgement?.status === 'accepted' ||
            (Array.isArray(w?.sanctions) && w.sanctions.some(s => s?.hospitalAcknowledgement?.status === 'accepted')) ||
            // FAL accepted by hospital
            (ds?.hospitalAccepted || ds?.acknowledgedByHospital) ||
            // Hospital has acknowledged the approval
            (ds?.hospitalAcknowledgement?.status === 'accepted' || ds?.hospitalAcknowledgement?.acknowledged);
          return (
            // FAL accepted, has docs, but no dispatch yet
            (falAcceptedScrutiny && hasDocs && !hasDispatch && !w.disbursal?.status) ||
            // Documents uploaded but not yet dispatched
            (hasDocs && !hasDispatch && !w.disbursal?.status) ||
            // Documents under scrutiny
            (ds?.status === 'under_scrutiny' || ds?.scrutinyPending) ||
            // Documents pending dispatch
            (ds?.documentsUploaded && !hasDispatch) ||
            // Applications with documents but no dispatch
            (ds?.documentsCount > 0 && !hasDispatch) ||
            // Documents ready for dispatch
            (ds?.documentsReady && !hasDispatch)
          );
        case 'pod_pending':
          const podAcked = pod?.status === 'received' || 
                         pod?.acknowledgedAt ||
                         // POD acknowledged
                         (pod?.acknowledged || pod?.received) ||
                         // POD confirmed
                         (pod?.status === 'confirmed' || pod?.status === 'acknowledged');
          const hasDispatchPOD = !!dispatch?.status || (Array.isArray(dispatch) && dispatch.length > 0);
          return (
            // Has dispatch but POD not acknowledged
            (hasDispatchPOD && !podAcked) ||
            // POD pending
            (pod?.status === 'pending' || pod?.status === 'awaiting') ||
            // POD not yet received
            (!pod?.status || pod?.status === 'pending' || pod?.status === 'awaiting') ||
            // Dispatch sent but POD not confirmed
            (hasDispatchPOD && (!pod?.status || pod?.status === 'pending')) ||
            // Applications with dispatch but no POD
            (hasDispatchPOD && !podAcked && !w.disbursal?.status)
          );
        default:
          // Creative fallback - try to match based on application characteristics
          
          switch (pendingDataFilter) {
            case 'diagnosis_approved':
              // Fallback: Any application with medical approval
              return app?.status === 'medical_approved' || mv.status === 'approved' || app?.status === 'diagnosis_approved';
            case 'query_raised':
              // Fallback: Any application with pending status
              return app?.status === 'pending_financial_clarification' || app?.status === 'under_review' || fin.status === 'pending';
            case 'query_reply':
              // Fallback: Any application under review
              return app?.status === 'under_review' || fin.status === 'under_review' || fin.underReview;
            case 'finance_approved':
              // Fallback: Any application with approved status
              return fin.status === 'approved' || app?.status === 'financial_approved' || app?.status === 'approved';
            case 'finance_approval_pending':
              // Fallback: Any application with pending financial status
              return fin.status === 'pending' || fin.status === 'under_review' || app?.status === 'under_financial_review';
            case 'dispersal_pending':
              // Fallback: Any application with approved status but pending dispersal
              return (fin.status === 'approved' || app?.status === 'financial_approved') && (w.disbursal?.status === 'pending' || w.disbursal?.status === 'initiated');
            case 'fal_generated':
              // Fallback: Any application with discharge settlement
              return ds?.status || ds?.finalApprovalLetterUrl || ds?.finalApprovalLetterId;
            case 'enach_pending':
              // Fallback: Any application with eNACH workflow
              return w.enach?.status || w.eNach?.status || (ds?.status && !w.enach?.status);
            case 'enach_completed':
              // Fallback: Any application with completed eNACH
              return w.enach?.status === 'completed' || w.eNach?.status === 'completed';
            case 'scrutiny_pending':
              // Fallback: Any application with documents but no dispatch
              return (ds?.documentsUploaded || ds?.documentsCount > 0) && !w.dispatch?.status;
            case 'pod_pending':
              // Fallback: Any application with dispatch but no POD
              return w.dispatch?.status && !w.proofOfDispatch?.status;
        default:
          return true;
          }
      }
    });
  }, [pendingDataApps, pendingDataFilter]);

  const filterTabs = [
    { key: 'all', label: 'All', icon: Activity, count: pendingDataCounts.all || 0 },
    { key: 'new', label: 'New Applications', icon: FileText, count: pendingDataCounts.new || 0 },
    { key: 'diagnosis_approved', label: 'Diagnosis Approved', icon: CheckCircle, count: pendingDataCounts.diagnosis_approved || 0 },
    { key: 'query_raised', label: 'Query Raised', icon: AlertTriangle, count: pendingDataCounts.query_raised || 0 },
    { key: 'query_reply', label: 'Query Reply Received', icon: CheckCircle, count: pendingDataCounts.query_reply || 0 },
    { key: 'finance_approved', label: 'Finance Approved', icon: CheckCircle, count: pendingDataCounts.finance_approved || 0 },
    { key: 'finance_approval_pending', label: 'Finance Approval Pending', icon: Clock, count: pendingDataCounts.finance_approval_pending || 0 },
    { key: 'dispersal_pending', label: 'Dispersal Pending', icon: CreditCard, count: pendingDataCounts.dispersal_pending || 0 },
    { key: 'fal_generated', label: 'FAL Generated', icon: FileText, count: pendingDataCounts.fal_generated || 0 },
    { key: 'enach_pending', label: 'E-NACH Pending', icon: CreditCard, count: pendingDataCounts.enach_pending || 0 },
    { key: 'enach_completed', label: 'E-NACH Completed', icon: CheckCircle, count: pendingDataCounts.enach_completed || 0 },
    { key: 'scrutiny_pending', label: 'Scrutiny Pending', icon: Activity, count: pendingDataCounts.scrutiny_pending || 0 },
    { key: 'pod_pending', label: 'POD Pending', icon: Building, count: pendingDataCounts.pod_pending || 0 },
  ];

  const filteredApplications = getFilteredApplications();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Pending Data</h1>
          <p className="text-gray-600">Applications requiring additional data or clarification</p>
        </div>
        <div className="flex items-center space-x-3">
          <input
            type="text"
            placeholder="Search by ID, patient, diagnosis, hospital, doctor"
            value={pendingDataSearch}
            onChange={(e) => setPendingDataSearch(e.target.value)}
            className="input-field w-72"
          />
          <button onClick={loadFinanceApplications} className="btn-outline">
            <RefreshCw className="h-4 w-4 mr-2"/>
            Refresh
          </button>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 sticky top-16 z-30">
        <div className="px-4 pt-3 pb-3 overflow-x-auto">
          <div className="flex flex-wrap items-center gap-2">
            {filterTabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = pendingDataFilter === tab.key;
              
              return (
                <button
                  key={tab.key}
                  onClick={() => setPendingDataFilter(tab.key)}
                  className={`px-4 py-2 text-sm rounded-lg border transition-colors flex items-center space-x-2 ${
                    isActive
                      ? 'bg-teal-50 text-teal-700 border-teal-200'
                      : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span>{tab.label}</span>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    isActive 
                      ? 'bg-teal-100 text-teal-800' 
                      : 'bg-gray-100 text-gray-700'
                  }`}>
                    {tab.count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {pendingDataLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mr-2"></div>
            <span className="text-gray-600">Loading finance applications...</span>
          </div>
        ) : filteredApplications.length === 0 ? (
          <div className="text-center py-12">
            <Clock className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Finance Applications Found</h3>
            <p className="text-gray-600 mb-4">
              {pendingDataSearch 
                ? `No finance applications match "${pendingDataSearch}"` 
                : `No applications in ${filterTabs.find(t => t.key === pendingDataFilter)?.label || 'this category'}`
              }
            </p>
            <p className="text-sm text-gray-500">Shows all applications in finance pipeline: approved, pending, under review, or in financial assessment stage.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Application ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Patient</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hospital</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Claim Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Finance Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Workflow Stage</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Submitted</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredApplications.map((application) => {
                  const w = application?.workflow || {};
                  const fin = w.financialVerification || {};
                  
                  return (
                    <tr key={application.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {application.applicationId}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 font-medium">
                          {application.patient?.name || 'N/A'}
                        </div>
                        <div className="text-xs text-gray-500">
                          {application.patient?.age ? `${application.patient.age}Y` : ''}
                          {application.patient?.gender ? `, ${application.patient.gender}` : ''}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 flex items-center">
                          <Building className="h-3 w-3 mr-1 text-gray-400" />
                          {application.hospitalName || '—'}
                        </div>
                        <div className="text-xs text-gray-500">
                          {application.medical?.doctor?.name ? `Dr. ${application.medical.doctor.name}` : ''}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatAmount(application.medical?.claimAmount || application.medical?.amount || 0)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {renderFinanceStatusChip(fin, application)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {renderWorkflowStageChips(application)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {application.submittedAt?.toDate 
                          ? application.submittedAt.toDate().toLocaleDateString() 
                          : (application.submittedAt 
                              ? new Date(application.submittedAt).toLocaleDateString() 
                              : '—'
                            )
                        }
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => navigate(`/dashboard/financial/review/${application.applicationId}`)}
                          className="text-teal-600 hover:text-teal-900"
                        >
                          Review
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PendingDataTab;
